import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { useAlertsRef } from '../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { selectCommentaryV2 } from '../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectAlerts } from '../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import { selectIsInspectifyOrder } from '../redux/features/selectors/orderSelectors.ts';
import { areEquivalentAlertPages, getAlertPageKey } from '@aloft/toolkit-shared';

import { routeToStepMap } from './validation/consts.ts';
import {
  clearOffPageTriggeredAlerts,
  clearPendingNavBlockToastTypes,
  clearToastSuppressedAlertTypes,
  retriggerAlertToasts,
} from '../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../redux/hooks.ts';
import { store } from '../redux/store.ts';
import {
  getActiveAlertsForRoute,
  useAppraisifyNavigation,
} from './validation/useAppraisifyNavigation.ts';

export const useSyncTriggeredAlertsToCurrentRoute = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const dismissedAlerts = useAppSelector(selectAlerts).dismissedAlerts ?? [];
  const finalizedExplanations = useAppSelector((state) => state.ui.finalizedExplanations);
  const explanationContext = { dismissedAlerts, finalizedExplanations };
  const alertsRef = useAlertsRef();
  const { getCurrentAppraisifyRoute } = useAppraisifyNavigation();
  const lastSyncedPathnameRef = useRef<string | undefined>();

  useLayoutEffect(() => {
    if (!isInspectifyOrder) {
      return;
    }

    const alerts = alertsRef.current;
    const currentRoute = getCurrentAppraisifyRoute(pathname);
    if (!currentRoute) {
      return;
    }

    const pathnameChanged = lastSyncedPathnameRef.current !== pathname;

    if (pathnameChanged) {
      dispatch(clearToastSuppressedAlertTypes());
      const pendingNavBlockToastTypes = store.getState().ui.pendingNavBlockToastTypes;
      if (pendingNavBlockToastTypes.length > 0) {
        const alertsByType = new Map(
          Object.values(alerts)
            .flat()
            .map((alert) => [alert.type, alert]),
        );
        const pendingRelevantOnCurrentRoute = pendingNavBlockToastTypes.some((type) => {
          const alert = alertsByType.get(type);
          return (
            alert &&
            currentRoute &&
            areEquivalentAlertPages(getAlertPageKey(alert, routeToStepMap), currentRoute)
          );
        });
        if (!pendingRelevantOnCurrentRoute) {
          dispatch(clearPendingNavBlockToastTypes());
        }
      }
    }

    lastSyncedPathnameRef.current = pathname;

    dispatch(
      clearOffPageTriggeredAlerts({
        route: currentRoute,
        alertsPerSection: alerts,
        alertsCommentary,
        explanationContext,
      }),
    );

    if (pathnameChanged && store.getState().ui.pendingNavBlockToastTypes.length > 0) {
      const alertsByType = new Map(
        Object.values(alerts)
          .flat()
          .map((alert) => [alert.type, alert]),
      );
      const pendingOnCurrentRoute = store
        .getState()
        .ui.pendingNavBlockToastTypes.filter((type) => {
          const alert = alertsByType.get(type);
          return (
            alert &&
            areEquivalentAlertPages(getAlertPageKey(alert, routeToStepMap), currentRoute)
          );
        });
      if (pendingOnCurrentRoute.length > 0) {
        const triggered = store.getState().ui.triggeredAlerts;
        const typesToRetrigger = getActiveAlertsForRoute(
          alerts,
          currentRoute,
          alertsCommentary,
          false,
          explanationContext,
        )
          .map((alert) => alert.type)
          .filter((type) => triggered.includes(type));
        if (typesToRetrigger.length > 0) {
          dispatch(retriggerAlertToasts(typesToRetrigger));
        }
      }
    }
  }, [
    alertsCommentary,
    explanationContext,
    alertsRef,
    dispatch,
    getCurrentAppraisifyRoute,
    isInspectifyOrder,
    pathname,
  ]);
};
